import { Outlet, data, redirect } from 'react-router'
import type { Route } from './+types/LocaleLayout'
import { LocaleProvider } from '@/lib/i18n/LocaleContext'
import { DEFAULT_LOCALE, isLocale, type Locale } from '@/lib/i18n/messages'
import { stripLocale } from '@/lib/i18n/localePath'
import { createContentLoader } from '@/lib/cms/prefetch.server'
import { contentQueries } from '@/lib/cms/queries'
import type { SeoDefaults } from '@/lib/seo'
import NotFoundPage from '@/pages/NotFoundPage'
import { RootLayout } from './RootLayout'

/**
 * Route layout that resolves the content locale from the URL and prefetches
 * the site-wide globals (header, footer, site settings) every page uses.
 *
 *  - No prefix              → the default locale.
 *  - `/<secondary>/…`       → the secondary locale.
 *  - `/<default>/…`         → 301 to the unprefixed URL (one URL per page).
 *  - any other first segment (e.g. `/foobar`) → 404 inside the site chrome.
 */
export async function loader({ params, url }: Route.LoaderArgs) {
  const lang = (params as { lang?: string }).lang
  if (lang === DEFAULT_LOCALE) {
    // `url`, not `request.url`: with v8_passThroughRequests a client-side
    // navigation arrives as `/<locale>/about.data`; `url` is normalised.
    return redirect(`${stripLocale(url.pathname)}${url.search}`, 301)
  }
  const unknownPrefix = lang !== undefined && !isLocale(lang)
  const locale: Locale = lang !== undefined && isLocale(lang) ? lang : DEFAULT_LOCALE

  const content = createContentLoader(locale)
  await content.prefetch([contentQueries.header(), contentQueries.footer(), contentQueries.siteSettings()])
  const settings = content.get(contentQueries.siteSettings())
  const seoDefaults: SeoDefaults = {
    title: settings?.defaultSeoTitle,
    description: settings?.defaultSeoDescription,
    image: settings?.defaultOgImageUrl,
  }
  const payload = { locale, unknownPrefix, seoDefaults, dehydratedState: content.dehydrate() }
  return unknownPrefix ? data(payload, { status: 404 }) : payload
}

export default function LocaleLayout({ loaderData }: Route.ComponentProps) {
  return (
    <LocaleProvider locale={loaderData.locale}>
      {loaderData.unknownPrefix ? <NotFoundPage /> : <RootLayout><Outlet /></RootLayout>}
    </LocaleProvider>
  )
}
